import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FiArrowUpRight } from '@react-icons/all-files/fi/FiArrowUpRight'
import { FiStar } from '@react-icons/all-files/fi/FiStar'
import reviews from '../../../utilities/clientReviews'
import { useCache } from '../../../utilities/useCache'
import { formatReviewDate } from '../../../utilities/reviewDate'
import { refreshURL } from '../../../store/slices/cache'
import * as gtag from '../../../lib/gtag'
import Container from '../foundations/Container'
import Action from '../primitives/Action'
import styles from './ReviewProof.module.scss'

const GOOGLE_REVIEWS_URL = 'https://search.google.com/local/reviews?placeid=ChIJs3yjQeut2YgRok3VQMIWH2Q'
const REVIEW_REFRESH_INTERVAL = 15 * 60 * 1000

const Stars = ({ rating = 5 }) => (
  <span className={styles.stars} role='img' aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, index) => (
      <FiStar key={index} aria-hidden='true' data-active={index < rating} />
    ))}
  </span>
)

const fallbackData = filter => ({
  reviews: reviews
    .filter(review => !filter.length || review.tags?.some(tag => filter.includes(tag)))
    .slice(0, 3)
    .map(review => ({
      id: `${review.name}-${review.date}`,
      author: review.name,
      rating: review.stars,
      comment: review.body,
      date: review.date,
      location: 'South Florida',
      sourceUrl: review.link
    }))
})

const ReviewProof = ({
  description,
  enableLiveData = true,
  eyebrow = 'Live Google feedback',
  filter = [],
  heading = 'Trusted for events across South Florida',
  initialData,
  pagePath = '/',
  ratings,
  reviewsUrl = GOOGLE_REVIEWS_URL,
  sectionId = 'speaker-client-reviews',
  layout = 'grid',
}) => {
  const dispatch = useDispatch()
  const [hasHydrated, setHasHydrated] = useState(false)
  const cacheUrl = enableLiveData
    ? `/google-review/public?page=${encodeURIComponent(pagePath)}&limit=3`
    : ''
  const cachedData = useCache(cacheUrl)
  // The cache can already contain fresher review data when client hydration
  // begins, while the static HTML contains `initialData` or the local
  // fallback. Keep the first client render identical to the server and switch
  // to the live cache only after React has attached to the document.
  const liveData = hasHydrated && cachedData?.reviews?.length
    ? cachedData
    : initialData?.reviews?.length
      ? initialData
      : fallbackData(filter)
  const visibleReviews = liveData.reviews
  const source = liveData.source
  const lastSynced = formatReviewDate(source?.lastSyncedAt)
  const selectionNote = description || (!source
    ? 'Stored testimonials remain visible while live Google review data is temporarily unavailable.'
    : liveData.selection?.usedFallback
      ? 'Showing the best recent Google feedback until more page-specific reviews are available.'
      : 'Selected automatically for page relevance, recency, rating, and service area.')
  const ratingItems = ratings || [
    {
      label: 'Current rating',
      value: source?.averageRating ? `${source.averageRating} / 5` : 'Google',
    },
    {
      label: 'Google reviews',
      value: source?.totalReviewCount?.toLocaleString?.('en-US') || 'Verified',
    },
    {
      label: 'Latest sync',
      value: lastSynced || 'Live',
    },
  ]

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  useEffect(() => {
    if (!enableLiveData) return undefined
    const interval = window.setInterval(() => {
      dispatch(refreshURL(cacheUrl))
    }, REVIEW_REFRESH_INTERVAL)
    return () => window.clearInterval(interval)
  }, [cacheUrl, dispatch, enableLiveData])

  return (
    <section className={styles.section} aria-labelledby={sectionId}>
      <Container>
        <div className={styles.heading}>
          <div>
            {eyebrow && <span>{eyebrow}</span>}
            <h2 id={sectionId}>{heading}</h2>
            <p className={styles.selectionNote}>{selectionNote}</p>
          </div>
          <div className={styles.ratings} aria-label='Public review ratings'>
            {ratingItems.map(item => (
              <span key={item.label}>
                <strong>{item.value}</strong>
                <small>{item.label}</small>
              </span>
            ))}
          </div>
        </div>

        <div className={`${styles.grid} ${layout === 'featured' ? styles.featuredGrid : ''}`.trim()}>
          {visibleReviews.map(review => (
            <article className={styles.review} key={review.id || `${review.author}-${review.date}`}>
              <div className={styles.reviewHead}>
                <Stars rating={review.rating} />
                <span>Google · {review.location || 'South Florida'}</span>
              </div>
              <p>{review.comment}</p>
              <div className={styles.reviewFooter}>
                <span>
                  <strong>{review.author}</strong>
                  <small>{formatReviewDate(review.date)}</small>
                </span>
                <Action
                  href={review.sourceUrl || reviewsUrl}
                  external
                  target='_blank'
                  rel='noreferrer noopener'
                  variant='plain'
                  wrapContent={false}
                  ariaLabel={`View Google reviews after ${review.author}'s feedback`}
                >
                  <FiArrowUpRight aria-hidden='true' />
                </Action>
              </div>
            </article>
          ))}
        </div>

        <div className={styles.action}>
          <Action
            href={reviewsUrl}
            external
            target='_blank'
            variant='secondary'
            icon={FiArrowUpRight}
            onClick={() => gtag.event({
              action: 'view_reviews',
              label: 'leads',
              value: 1,
              category: 'orders'
            })}
          >
            View All Google Reviews
          </Action>
        </div>
      </Container>
    </section>
  )
}

export default ReviewProof
